import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { fetchProvidersByUniqueId } from '../../ApiHandler/PatientFunctions';
import { createReservation } from '../../ApiHandler/ReservationFunctions';
import '../../global.css';

const localizer = momentLocalizer(moment);

const PatientForm = () => {
    const location = useLocation();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [providers, setProviders] = useState([]);
    const [uniqueId, setUniqueId] = useState('');
    const [hasUniqueId, setHasUniqueId] = useState(false);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        pincode: ''
    });

    // Google Places autocomplete
    const addressInputRef = useRef(null);
    const autocompleteRef = useRef(null);

    useEffect(() => {
        // Extract unique_id from URL query params
        const searchParams = new URLSearchParams(location.search);
        const id = searchParams.get('unique_id');
        if (id) {
            setUniqueId(id);
            setHasUniqueId(true);
        } else {
            setHasUniqueId(false);
        }
    }, [location.search]);

    useEffect(() => {
        // Initialize Google Places Autocomplete
        const loadGooglePlaces = async () => {
            if (!window.google) {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);

                script.onload = () => initializeAutocomplete();
            } else {
                initializeAutocomplete();
            }
        };

        const initializeAutocomplete = () => {
            if (addressInputRef.current && window.google) {
                autocompleteRef.current = new window.google.maps.places.Autocomplete(
                    addressInputRef.current,
                    { types: ['address'] }
                );

                autocompleteRef.current.addListener('place_changed', () => {
                    const place = autocompleteRef.current.getPlace();
                    if (place && place.formatted_address) {
                        setFormData(prev => ({
                            ...prev,
                            address: place.formatted_address
                        }));

                        // Extract pincode
                        const postalCode = place.address_components?.find(
                            component => component.types.includes('postal_code')
                        );
                        if (postalCode) {
                            setFormData(prev => ({
                                ...prev,
                                pincode: postalCode.long_name
                            }));
                        }
                    }
                });
            }
        };

        loadGooglePlaces();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNext = async () => {
        // Validate form
        if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.pincode) {
            toast.error('Please fill in all fields');
            return;
        }

        if (!uniqueId) {
            toast.error('Invalid unique ID');
            return;
        }

        // Fetch providers
        setLoading(true);
        try {
            const response = await fetchProvidersByUniqueId(uniqueId);
            if (response.success && response.providers) {
                setProviders(response.providers);
                generateCalendarEvents(response.providers);
                setStep(2);
            } else {
                toast.info('No providers found for this clinic');
            }
        } catch (error) {
            console.error('Error fetching providers:', error);
            toast.error('Failed to fetch providers');
        } finally {
            setLoading(false);
        }
    };

    const generateCalendarEvents = (providersList) => {
        const events = [];
        const today = moment().startOf('day');

        providersList.forEach(provider => {
            try {
                const availability = typeof provider.availability === 'string'
                    ? JSON.parse(provider.availability)
                    : provider.availability;

                if (Array.isArray(availability) && availability.length > 0) {
                    // Generate events for the next 4 weeks
                    for (let week = 0; week < 4; week++) {
                        availability.forEach(slot => {
                            const dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
                                .indexOf(slot.day);

                            if (dayOfWeek !== -1) {
                                const eventDate = today.clone().add(week, 'weeks').day(dayOfWeek);

                                // Only add future events
                                if (eventDate.isAfter(moment())) {
                                    const [startHour, startMin] = slot.start.split(':');
                                    const [endHour, endMin] = slot.end.split(':');

                                    const startTime = eventDate.clone().hour(parseInt(startHour)).minute(parseInt(startMin));
                                    const endTime = eventDate.clone().hour(parseInt(endHour)).minute(parseInt(endMin));

                                    events.push({
                                        title: provider.clinic_name,
                                        start: startTime.toDate(),
                                        end: endTime.toDate(),
                                        resource: {
                                            provider: provider,
                                            slot: slot
                                        }
                                    });
                                }
                            }
                        });
                    }
                }
            } catch (e) {
                console.error('Error parsing availability for provider:', provider.clinic_name, e);
            }
        });

        setCalendarEvents(events);
    };

    const handleSelectEvent = (event) => {
        // Get all providers available at this time slot
        const selectedDateTime = moment(event.start);
        const availableProviders = calendarEvents
            .filter(e => moment(e.start).isSame(selectedDateTime, 'minute'))
            .map(e => e.resource.provider);

        setSelectedSlot({
            dateTime: selectedDateTime,
            providers: availableProviders
        });
        setShowModal(true);
    };

    const handleBookProvider = async (provider) => {
        try {
            // Get the event details to find start and end times
            const selectedEvent = calendarEvents.find(
                e => e.resource.provider.clinic_id === provider.clinic_id &&
                     moment(e.start).isSame(selectedSlot.dateTime, 'minute')
            );

            if (!selectedEvent) {
                toast.error('Unable to find time slot details');
                return;
            }

            const reservationData = {
                patient_name: formData.name,
                patient_email: formData.email,
                patient_phone: formData.phone,
                patient_address: formData.address,
                patient_pincode: formData.pincode,
                clinic_id: provider.clinic_id,
                scheduled_date: moment(selectedEvent.start).format('YYYY-MM-DD'),
                scheduled_time_start: moment(selectedEvent.start).format('HH:mm:ss'),
                scheduled_time_end: moment(selectedEvent.end).format('HH:mm:ss')
            };

            const response = await createReservation(reservationData);

            if (response.success) {
                toast.success(`Reservation confirmed for ${provider.clinic_name} on ${selectedSlot.dateTime.format('MMMM Do YYYY, h:mm a')}`);
                setShowModal(false);
                
                // Optionally reset form or redirect
                setTimeout(() => {
                    setStep(1);
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        address: '',
                        pincode: ''
                    });
                }, 2000);
            }
        } catch (error) {
            console.error('Error booking provider:', error);
            toast.error('Failed to create reservation. Please try again.');
        }
    };

    const eventStyleGetter = (event) => {
        return {
            style: {
                backgroundColor: '#667eea',
                borderRadius: '4px',
                opacity: 0.8,
                color: 'white',
                border: '0px',
                display: 'block',
                fontSize: '0.85rem',
                padding: '2px 5px'
            }
        };
    };

    // If no unique_id is provided, show error page
    if (!hasUniqueId) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={3000} />
                <div style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '3rem',
                        maxWidth: '500px',
                        width: '100%',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '4rem',
                            color: '#667eea',
                            marginBottom: '1rem'
                        }}>
                            ⚠️
                        </div>
                        <h2 style={{
                            color: '#333',
                            fontSize: '1.8rem',
                            marginBottom: '1rem'
                        }}>
                            Access Denied
                        </h2>
                        <p style={{
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: '1.6'
                        }}>
                            This page can only be accessed with a valid clinic link. Please contact your healthcare provider for the correct booking link.
                        </p>
                    </div>
                </div>
            </>
        );
    }


    if (step === 1) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={3000} />
                <div style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '2.5rem',
                        maxWidth: '500px',
                        width: '100%',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                    }}>
                        <h2 style={{
                            textAlign: 'center',
                            marginBottom: '2rem',
                            color: '#333',
                            fontSize: '1.8rem'
                        }}>
                            Patient Information
                        </h2>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#555'
                                }}>
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your full name"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        transition: 'border-color 0.3s',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#555'
                                }}>
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter your email"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        transition: 'border-color 0.3s',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#555'
                                }}>
                                    Phone Number *
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter your phone number"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        transition: 'border-color 0.3s',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#555'
                                }}>
                                    Address *
                                </label>
                                <input
                                    ref={addressInputRef}
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    placeholder="Start typing your address..."
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        transition: 'border-color 0.3s',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#555'
                                }}>
                                    Pincode *
                                </label>
                                <input
                                    type="text"
                                    name="pincode"
                                    value={formData.pincode}
                                    onChange={handleInputChange}
                                    placeholder="Auto-filled from address"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: '#f9f9f9',
                                        transition: 'border-color 0.3s',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                />
                            </div>

                            <button
                                onClick={handleNext}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    backgroundColor: loading ? '#ccc' : '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    marginTop: '1rem',
                                    transition: 'background-color 0.3s',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) e.target.style.backgroundColor = '#5568d3';
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) e.target.style.backgroundColor = '#667eea';
                                }}
                            >
                                {loading ? 'Loading...' : 'Next'}
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (step === 2) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={3000} />
                <div style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '1rem 2rem'
                }}>
                    <div style={{
                        maxWidth: '1400px',
                        margin: '0 auto'
                    }}>
                        <button
                            onClick={() => setStep(1)}
                            style={{
                                backgroundColor: 'white',
                                color: '#667eea',
                                border: '2px solid white',
                                borderRadius: '8px',
                                padding: '0.5rem 1rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginBottom: '0rem',
                                transition: 'all 0.3s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#667eea';
                            }}
                        >
                            ← Back
                        </button>

                        <h2 style={{
                            textAlign: 'center',
                            marginBottom: '0.5rem',
                            color: 'white',
                            fontSize: '1.75rem'
                        }}>
                            Select Available Time Slot
                        </h2>

                        <p style={{
                            textAlign: 'center',
                            color: 'rgba(255,255,255,0.9)',
                            marginBottom: '1rem',
                            fontSize: '0.95rem'
                        }}>
                            Click on any available slot to view provider details and book
                        </p>

                        {providers.length === 0 ? (
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                padding: '3rem',
                                textAlign: 'center',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                            }}>
                                <p style={{ fontSize: '1.2rem', color: '#666' }}>
                                    No providers available at this time.
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                minHeight: '600px'
                            }}>
                                <Calendar
                                    localizer={localizer}
                                    events={calendarEvents}
                                    startAccessor="start"
                                    endAccessor="end"
                                    style={{ height: '550px' }}
                                    onSelectEvent={handleSelectEvent}
                                    eventPropGetter={eventStyleGetter}
                                    views={['month', 'week', 'day']}
                                    defaultView='week'
                                    step={60}
                                    showMultiDayTimes
                                    min={new Date(2026, 0, 1, 8, 0, 0)}
                                    max={new Date(2026, 0, 1, 20, 0, 0)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Modal for provider selection */}
                    {showModal && selectedSlot && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1000,
                                padding: '1rem'
                            }}
                            onClick={() => setShowModal(false)}
                        >
                            <div
                                style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    padding: '2rem',
                                    maxWidth: '800px',
                                    width: '100%',
                                    maxHeight: '80vh',
                                    overflowY: 'auto',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '1.5rem',
                                    borderBottom: '2px solid #f0f0f0',
                                    paddingBottom: '1rem'
                                }}>
                                    <div>
                                        <h3 style={{
                                            color: '#333',
                                            margin: '0 0 0.5rem 0',
                                            fontSize: '1.5rem'
                                        }}>
                                            Available Providers
                                        </h3>
                                        <p style={{
                                            color: '#667eea',
                                            margin: 0,
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}>
                                            {selectedSlot.dateTime.format('dddd, MMMM Do YYYY')}
                                            <br />
                                            {selectedSlot.dateTime.format('h:mm A')}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        style={{
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            fontSize: '2rem',
                                            color: '#999',
                                            cursor: 'pointer',
                                            padding: '0',
                                            lineHeight: '1'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>

                                {selectedSlot.providers.length === 0 ? (
                                    <p style={{ textAlign: 'center', color: '#666' }}>
                                        No providers available for this slot
                                    </p>
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem'
                                    }}>
                                        {selectedSlot.providers.map((provider, index) => (
                                            <div
                                                key={provider.clinic_id || index}
                                                style={{
                                                    border: '2px solid #f0f0f0',
                                                    borderRadius: '10px',
                                                    padding: '1.25rem',
                                                    transition: 'all 0.2s',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.borderColor = '#667eea';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.borderColor = '#f0f0f0';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    gap: '1rem',
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <div style={{ flex: '1', minWidth: '200px' }}>
                                                        <h4 style={{
                                                            color: '#667eea',
                                                            margin: '0 0 0.5rem 0',
                                                            fontSize: '1.2rem',
                                                            fontWeight: '700'
                                                        }}>
                                                            {provider.clinic_name}
                                                        </h4>
                                                        {provider.title && (
                                                            <p style={{
                                                                color: '#888',
                                                                margin: '0 0 1rem 0',
                                                                fontSize: '0.9rem'
                                                            }}>
                                                                {provider.title}
                                                            </p>
                                                        )}

                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'auto 1fr',
                                                            gap: '0.5rem 1rem',
                                                            fontSize: '0.9rem'
                                                        }}>
                                                            {provider.specialization && (
                                                                <>
                                                                    <strong style={{ color: '#555' }}>Specialization:</strong>
                                                                    <span style={{ color: '#666' }}>{provider.specialization}</span>
                                                                </>
                                                            )}
                                                            {provider.provider_type && (
                                                                <>
                                                                    <strong style={{ color: '#555' }}>Provider Type:</strong>
                                                                    <span style={{ color: '#666' }}>{provider.provider_type}</span>
                                                                </>
                                                            )}
                                                            {provider.service_areas && (
                                                                <>
                                                                    <strong style={{ color: '#555' }}>Service Area:</strong>
                                                                    <span style={{ color: '#666' }}>
                                                                        {provider.service_areas}
                                                                        {provider.travel_radius && ` (${provider.travel_radius} mi)`}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleBookProvider(provider)}
                                                        style={{
                                                            padding: '0.75rem 2rem',
                                                            backgroundColor: '#667eea',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontSize: '1rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.2s',
                                                            whiteSpace: 'nowrap',
                                                            alignSelf: 'center'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#5568d3'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#667eea'}
                                                    >
                                                        Book Now
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    }

    return null;
};

export default PatientForm;
